import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Button,
  Textarea,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  Flex,
  IconButton,
  useToast,
  FormLabel,
  Badge,
  HStack,
} from '@chakra-ui/react';
import { DeleteIcon, CheckIcon } from '@chakra-ui/icons';
import { format } from 'date-fns';
import { getData, postData, putData } from 'utils/httpClient';
import authService from 'services/authService';
import { primaryButtonProps, secondaryButtonProps } from '../../theme/adminUi';
import { isAlertPending } from '../../utils/vehicleAlerts';

const HEADER_BLUE = '#1e4a7a';
const HIGHLIGHT_BG = '#e8d4f0';

const formatAlertDate = (value) => {
  if (!value) return '—';
  try {
    return format(new Date(value), 'd/M/yyyy HH:mm:ss');
  } catch {
    return '—';
  }
};

const ServiceAlertsModal = ({
  isOpen,
  onClose,
  vehicleId,
  vehicleServiceId,
  onAlertsChanged,
}) => {
  const [alerts, setAlerts] = useState([]);
  const [newText, setNewText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);
  const toast = useToast();
  const canMarkSeen = authService.isAdmin();

  const loadAlerts = async () => {
    if (!vehicleId) return;
    try {
      setLoading(true);
      const response = await getData(`/vehicleAlerts/vehicle/${vehicleId}`, {
        skipLoader: true,
      });
      setAlerts(response?.payload?.alerts || []);
    } catch (err) {
      setAlerts([]);
      toast({
        title: 'No se pudieron cargar los avisos',
        description: err?.message || 'Verifique la conexión con el servidor.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && vehicleId) {
      setNewText('');
      loadAlerts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recargar al abrir
  }, [isOpen, vehicleId]);

  useEffect(() => {
    if (!isOpen && textareaRef.current) {
      textareaRef.current.blur();
    }
  }, [isOpen]);

  const handleAdd = async () => {
    const text = newText.trim();
    if (!text || !vehicleId) return;
    try {
      setSaving(true);
      const user = authService.getCurrentUser() || {};
      await postData(
        '/vehicleAlerts',
        {
          vehicleId: Number(vehicleId),
          vehicleServiceId: vehicleServiceId ? Number(vehicleServiceId) : null,
          text,
          createdByUserId: user?.id ? Number(user.id) : null,
        },
        { skipLoader: true },
      );
      setNewText('');
      await loadAlerts();
      onAlertsChanged?.();
      toast({
        title: 'Aviso agregado',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      textareaRef.current?.focus();
    } catch (err) {
      toast({
        title: 'Error al guardar aviso',
        description: err?.message || 'No se pudo guardar la nota.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleMarkSeen = async (alert) => {
    if (!canMarkSeen || !isAlertPending(alert)) return;
    try {
      await putData(
        `/vehicleAlerts/${alert.id}`,
        { seen: true },
        { skipLoader: true },
      );
      await loadAlerts();
      onAlertsChanged?.();
      toast({
        title: 'Aviso marcado como visto',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Error al marcar aviso',
        description: err?.message || 'Intente nuevamente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (alert) => {
    try {
      await putData(
        `/vehicleAlerts/${alert.id}`,
        { resolved: true },
        { skipLoader: true },
      );
      await loadAlerts();
      onAlertsChanged?.();
      toast({
        title: 'Aviso eliminado',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Error al eliminar aviso',
        description: err?.message || 'Intente nuevamente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleClose = () => {
    onAlertsChanged?.();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="2xl"
      isCentered
      blockScrollOnMount={false}
      scrollBehavior="inside"
     closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent maxW="720px" borderRadius="md">
        <ModalHeader
          bg="gray.100"
          borderBottom="1px solid"
          borderColor="gray.300"
          py={3}
          textAlign="center"
          fontSize="md"
          fontWeight="bold"
          letterSpacing="wide"
        >
          Avisos vigentes
        </ModalHeader>
        <ModalCloseButton top={2} right={2} />

        <ModalBody p={4} bg="white">
          <FormLabel fontSize="sm" fontWeight="semibold" mb={1}>
            Nueva nota / aviso
          </FormLabel>
          <Textarea
            ref={textareaRef}
            autoFocus
            size="sm"
            minH="96px"
            resize="vertical"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Escriba aquí la observación para el vehículo…"
            bg="white"
            border="1px solid"
            borderColor="gray.300"
            borderRadius="md"
            _focus={{
              borderColor: 'blue.400',
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
            }}
            isDisabled={!vehicleId || saving}
          />
          <Text fontSize="xs" color="gray.500" mt={1} mb={3}>
            Ctrl + Enter para guardar rápido
          </Text>

          <Flex justify="flex-end" mb={4}>
            <Button
              {...primaryButtonProps}
              onClick={handleAdd}
              isLoading={saving}
              isDisabled={!newText.trim() || !vehicleId}
              px={6}
            >
              Agregar aviso
            </Button>
          </Flex>

          <TableContainer
            border="1px solid"
            borderColor="gray.300"
            borderRadius="md"
            maxH="320px"
            overflowY="auto"
          >
            <Table size="sm" variant="simple" sx={{ borderCollapse: 'collapse' }}>
              <Thead position="sticky" top={0} zIndex={1}>
                <Tr>
                  <Th
                    bg={HEADER_BLUE}
                    color="white"
                    border="1px solid"
                    borderColor="gray.400"
                    w="170px"
                    fontSize="xs"
                    textTransform="none"
                    py={2}
                  >
                    Fecha
                  </Th>
                  <Th
                    bg={HEADER_BLUE}
                    color="white"
                    border="1px solid"
                    borderColor="gray.400"
                    fontSize="xs"
                    textTransform="none"
                    py={2}
                  >
                    Descripción
                  </Th>
                  <Th
                    bg={HEADER_BLUE}
                    color="white"
                    border="1px solid"
                    borderColor="gray.400"
                    w="140px"
                    fontSize="xs"
                    textTransform="none"
                    textAlign="center"
                    py={2}
                  >
                    Acciones
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan={3} border="1px solid" borderColor="gray.300">
                      <Text fontSize="sm" color="gray.500" py={4} textAlign="center">
                        Cargando...
                      </Text>
                    </Td>
                  </Tr>
                ) : alerts.length === 0 ? (
                  <Tr>
                    <Td colSpan={3} border="1px solid" borderColor="gray.300">
                      <Text fontSize="sm" color="gray.500" py={4} textAlign="center">
                        Sin avisos vigentes. Agregue una nota arriba.
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  alerts.map((alert) => {
                    const pending = isAlertPending(alert);
                    return (
                    <Tr
                      key={alert.id}
                      bg={pending ? HIGHLIGHT_BG : 'gray.50'}
                      opacity={pending ? 1 : 0.85}
                    >
                      <Td
                        fontSize="xs"
                        whiteSpace="nowrap"
                        border="1px solid"
                        borderColor="gray.300"
                        verticalAlign="middle"
                      >
                        {formatAlertDate(alert.createdAt)}
                      </Td>
                      <Td
                        fontSize="sm"
                        border="1px solid"
                        borderColor="gray.300"
                        verticalAlign="middle"
                        whiteSpace="pre-wrap"
                      >
                        <HStack align="start" spacing={2}>
                          <Text flex="1">{alert.text}</Text>
                          {!pending && (
                            <Badge colorScheme="gray" fontSize="10px" flexShrink={0}>
                              Visto
                            </Badge>
                          )}
                        </HStack>
                      </Td>
                      <Td
                        border="1px solid"
                        borderColor="gray.300"
                        textAlign="center"
                        verticalAlign="middle"
                      >
                        <HStack justify="center" spacing={1}>
                          {pending && canMarkSeen && (
                            <Button
                              size="xs"
                              leftIcon={<CheckIcon />}
                              colorScheme="green"
                              variant="outline"
                              onClick={() => handleMarkSeen(alert)}
                            >
                              Visto
                            </Button>
                          )}
                          <IconButton
                            aria-label="Eliminar aviso"
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="ghost"
                            color="red.500"
                            _hover={{ bg: 'red.50', color: 'red.600' }}
                            onClick={() => handleDelete(alert)}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.200" py={3}>
          <Button {...secondaryButtonProps} onClick={handleClose}>
            Cerrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ServiceAlertsModal;
